import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sparkles, ArrowLeft, Shield } from "lucide-react";
import { useTheme } from "../theme";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivacyPolicy() {
  const { isDark } = useTheme();
  const [appSettings, setAppSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => setAppSettings(r.data)).catch(() => {});
  }, []);

  const appName = appSettings?.app_name || "Labelled";
  const customContent = appSettings?.privacy_policy_content?.trim() || "";

  return (
    <div className="min-h-screen h-screen overflow-y-auto" style={{ background: isDark ? "#0f172a" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fce7f3 100%)" }} data-testid="privacy-page">
      <nav className="nav-bar">
        <Link to="/" className="nav-logo">
          <img src={`${API}/logo`} alt="" className="w-10 h-10 rounded-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <span>{appName}</span>
        </Link>
        <Link to="/" className="btn-secondary text-sm py-2 px-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Map
        </Link>
      </nav>

      <main className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-8" data-testid="privacy-content">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-black text-slate-800 dark:text-white" style={{ fontFamily: "Nunito" }}>Privacy Policy</h1>
          </div>

          {customContent ? (
            <div className="prose prose-slate dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed prose-headings:font-bold prose-headings:text-slate-800 dark:prose-headings:text-white prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2 prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2 prose-p:my-3 prose-ul:list-disc prose-ul:pl-5 prose-li:my-1 prose-strong:text-slate-800 dark:prose-strong:text-white prose-a:text-indigo-600 hover:prose-a:underline">
              <ReactMarkdown>{customContent}</ReactMarkdown>
            </div>
          ) : (
          <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p className="text-sm text-slate-500">Last updated: March 2026</p>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>1. Who We Are</h2>
              <p>{appName} is a community mapping platform for neurodivergent individuals. We are committed to protecting your privacy and handling your personal data responsibly in compliance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>2. What Data We Collect</h2>
              <p><strong>Pin Data:</strong> When you add a pin, we collect the town/area and your selected neurodivergent labels. We do NOT collect your exact address or GPS location. All locations are anonymised to a town-level area with a random offset.</p>
              <p><strong>Email Subscription (Optional):</strong> If you choose to subscribe to our newsletter, we collect your first name and email address. This is entirely optional and requires your explicit consent.</p>
              <p><strong>Cookie Data:</strong> We use cookies for essential functionality, analytics, and marketing purposes. You can control which cookies you accept via our cookie banner.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>3. How We Use Your Data</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Pin data</strong> is used to display community presence on the map at an area level. Individual locations are never shown.</li>
                <li><strong>Email addresses</strong> are used to send you community updates, event notifications, and promotional content. You can unsubscribe at any time.</li>
                <li><strong>Analytics cookies</strong> help us understand how people use the app so we can improve it.</li>
                <li><strong>Marketing cookies</strong> (Meta Pixel, TikTok Pixel) help us reach more people who may benefit from our community. These are only activated if you consent.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>4. Legal Basis for Processing</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Consent:</strong> For email marketing, marketing cookies, and analytics cookies.</li>
                <li><strong>Legitimate interest:</strong> For essential cookies and anonymous community mapping.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>5. Third-Party Services</h2>
              <p>We may share your data with the following third-party services:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Klaviyo:</strong> Email marketing platform — processes subscriber email addresses and names.</li>
                <li><strong>Meta (Facebook):</strong> Marketing pixel for advertising — only if you consent to marketing cookies.</li>
                <li><strong>TikTok:</strong> Marketing pixel for advertising — only if you consent to marketing cookies.</li>
                <li><strong>Stripe:</strong> Payment processing for featured event listings.</li>
                <li><strong>OpenStreetMap:</strong> Map tile data (no personal data shared).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>6. Your Rights</h2>
              <p>Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access your personal data</li>
                <li>Rectify inaccurate data</li>
                <li>Erase your data ("right to be forgotten")</li>
                <li>Restrict processing</li>
                <li>Data portability</li>
                <li>Object to processing</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p>To exercise these rights, contact us using the details below.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>7. Data Retention</h2>
              <p>Pin data is retained for as long as the service operates. Email subscriber data is retained until you unsubscribe. Cookie consent records are retained for 2 years as required by GDPR.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>8. Cookies</h2>
              <p><strong>Essential cookies:</strong> Required for the app to function (always active).</p>
              <p><strong>Analytics cookies:</strong> Help us understand app usage (requires consent).</p>
              <p><strong>Marketing cookies:</strong> Used by Meta and TikTok for advertising (requires consent).</p>
              <p>You can change your cookie preferences at any time via the cookie settings banner.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2" style={{ fontFamily: "Nunito" }}>9. Contact</h2>
              <p>For any privacy-related questions or data requests, please contact the site administrator through the app.</p>
            </section>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
